import { useState } from 'react';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { deserialize, serialize } from 'serializr';
import { generatePath } from 'react-router-dom';
import { generateAddressLabel } from 'views/Scope3/Category4&9/LocationModal';
import {
    EndMarketLocations,
    FarmBoxCommodityDestribution,
} from 'models/FarmBoxCommodityDestribution/farmBoxCommodityDestribution.model';
import { CommodityOverview } from 'models/Farmbox/overview.model';
import { LiveStockCommodityOverview } from 'models/Farmbox/LiveStock/liveStockOverview.model';

const FarmboxCommodityDistributionService = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [buttonLoading, setButtonLoading] = useState<boolean>(false);
    const [draftButtonLoading, setDraftButtonLoading] = useState<boolean>(false);
    const [commodityDistribution, setCommodityDistribution] =
        useState<FarmBoxCommodityDestribution>();
    const [farmboxOverview, setFarmboxOverview] = useState<CommodityOverview>();
    const [liveStockFarmboxOverview, setLiveStockFarmboxOverview] =
        useState<LiveStockCommodityOverview>();

    const addAddressLabel = (items?: EndMarketLocations[]) =>
        items?.map(item => ({
            ...item,
            addressLabel: item?.address
                ? generateAddressLabel(item?.address)
                : undefined,
        }));

    const updateCommodityDistribution = async (
        commodityId: number,
        payload: FarmBoxCommodityDestribution,
        isDraft?: boolean,
        isLiveStock?: boolean
    ) => {
        try {
            isDraft ? setDraftButtonLoading(true) : setButtonLoading(true);
            const desiredPayload = serialize(FarmBoxCommodityDestribution, payload);
            const url = generatePath(
                isLiveStock
                    ? ApiRoutes.LIVE_STOCK_COMMODITY_DISTRIBUTION
                    : ApiRoutes.COMMODITY_DISTRIBUTION,
                {
                    commodityId,
                }
            );
            await axiosInstance.put(
                url,
                { distribution: desiredPayload },
                {
                    params: { draft: isDraft },
                }
            );
            Notification({
                message: 'Commodity Distribution Updated Successfully',
                description: '',
                type: NotificationTypes.SUCCESS,
            });
            return true;
        } catch (error) {
            return false;
        } finally {
            isDraft ? setDraftButtonLoading(false) : setButtonLoading(false);
        }
    };
    const getCommodityDistribution = async (
        commodityId: number,
        isLiveStock?: boolean
    ) => {
        try {
            setLoading(true);
            const url = generatePath(
                isLiveStock
                    ? ApiRoutes.LIVE_STOCK_COMMODITY_DISTRIBUTION
                    : ApiRoutes.COMMODITY_DISTRIBUTION,
                {
                    commodityId,
                }
            );
            const response = await axiosInstance.get(url);
            const desiredPayload = deserialize(
                FarmBoxCommodityDestribution,
                response.data?.distribution
            );
            setCommodityDistribution(desiredPayload);
            const modifiedCategoryData: FarmBoxCommodityDestribution = {
                ...desiredPayload,
                routes: addAddressLabel(desiredPayload?.routes),
            };
            return modifiedCategoryData;
        } finally {
            setLoading(false);
        }
    };

    const getFarmboxOverview = async (commodityId: number) => {
        try {
            setLoading(true);
            const url = generatePath(ApiRoutes.FARMBOX_OVERVIEW, {
                commodityId,
            });
            const response = await axiosInstance.get(url);
            const desiredPayload = deserialize(
                CommodityOverview,
                response.data?.commodity
            );
            setFarmboxOverview(desiredPayload);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const approvalReport = async (commodityId: number, isLiveStock?: boolean) => {
        try {
            setLoading(true);
            const url = generatePath(
                isLiveStock
                    ? ApiRoutes.FARMBOX_LIVE_STOCK_APPROVAL_REPORT
                    : ApiRoutes.FARMBOX_APPROVAL_REPORT,
                {
                    commodityId,
                }
            );
            await axiosInstance.post(url);
            Notification({
                message: 'Commodity has been sent for approval',
                description: '',
                type: NotificationTypes.SUCCESS,
            });
        } finally {
            setLoading(false);
        }
    };

    const downloadApprovalReport = async (
        commodityId: number,
        isLiveStock?: boolean
    ) => {
        try {
            setLoading(true);
            const url = generatePath(
                isLiveStock
                    ? ApiRoutes.DOWNLOAD_FARMBOX_LIVE_STOCK_APPROVAL_REPORT
                    : ApiRoutes.DOWNLOAD_FARMBOX_APPROVAL_REPORT,
                {
                    commodityId,
                }
            );
            const response = await axiosInstance.get(url);
            window.open(response.data.url, '_blank');
        } finally {
            setLoading(false);
        }
    };

    const getFarmboxLiveStockOverview = async (commodityId: number) => {
        try {
            setLoading(true);
            const url = generatePath(ApiRoutes.LIVE_STOCK_FARMBOX_OVERVIEW, {
                commodityId,
            });
            const response = await axiosInstance.get(url);
            const desiredPayload = deserialize(
                LiveStockCommodityOverview,
                response.data?.commodity
            );
            setLiveStockFarmboxOverview(desiredPayload);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        buttonLoading,
        draftButtonLoading,
        updateCommodityDistribution,
        getCommodityDistribution,
        commodityDistribution,
        getFarmboxOverview,
        farmboxOverview,
        setFarmboxOverview,
        approvalReport,
        downloadApprovalReport,
        getFarmboxLiveStockOverview,
        liveStockFarmboxOverview,
        setLiveStockFarmboxOverview
    };
};

export default FarmboxCommodityDistributionService;
